import { billingFields } from '../helpers/checkoutForms';
import { STATES, METHODS } from '../constants/checkoutConstants';
import {
    emptyPaymentMethodAction,
    saveCreditCardAction,
    saveBankAccountAction,
    savePaypalAction,
    saveCheckAction,
    saveWireTransferAction,
    saveOnPlatformWireTransferAction,
    saveApplePayAction,
    completeCreditCardAction,
    completeBankAccountAction,
    saveStripePaymentMethodApiType,
    setPaymentProcessor,
} from './commonActions';

// Credit Cards
export function creditCardFormUpdated(field, value, isVerifying) {
    if (billingFields.includes(field)) {
        return { type: 'CHECKOUT_UPDATE_NEW_BILLING_FORM', field, value };
    } else if (isVerifying) {
        return { type: 'CHECKOUT_UPDATE_VERIFY_CARD_FORM', field, value };
    } else {
        return { type: 'CHECKOUT_UPDATE_NEW_CARD_FORM', field, value };
    }
}
export function creditCardFormCanceled() {
    return {
        type: 'CHECKOUT_CANCEL_CREDITCARD',
    };
}

export function selectCreditCard({ token, paymentProcessor }) {
    return {
        type: 'CHECKOUT_SELECT_CREDITCARD',
        creditCardState: STATES.STATE_SAVED,
        token,
        paymentProcessor,
    };
}

export function addCreditCard() {
    return { type: 'CHECKOUT_NEW_CREDITCARD' };
}

export function verifyCreditCard({ token, verifyCardForm }) {
    return {
        type: 'CHECKOUT_VERIFY_CREDITCARD',
        token,
        verifyCardForm,
    };
}

export function verifyBankAccount({ token, verifyBankAccountForm }) {
    return {
        type: 'CHECKOUT_VERIFY_BANKACCOUNT',
        token,
        verifyBankAccountForm,
    };
}

export function toggleACHFormSubmissionError(showError) {
    return {
        type: 'TOGGLE_ACH_FORM_SUBMISSION_ERROR',
        showError,
    };
}

export function creditCardDeleted(payload) {
    const { creditCardState, paymentMethodToken, isSelectedCardDeleted } = payload;

    if (!paymentMethodToken) {
        return emptyPaymentMethodAction({
            creditCardState,
            method: METHODS.METHOD_CREDIT_CARD,
        });
    } else if (isSelectedCardDeleted) {
        return selectCreditCard(paymentMethodToken);
    }
    return { type: 'NO_CHANGE' };
}

export function creditCardSaved({ token, paymentProcessor }) {
    return saveCreditCardAction({
        token,
        paymentProcessor,
    });
}

export function changeBankAccountSection(bankAccountSectionSelected) {
    return { type: 'CHECKOUT_CHANGE_BANKACCOUNT_SECTION', bankAccountSectionSelected };
}

export function selectBankAccountWireOrCheck(bankAccountMethodSelected) {
    return { type: 'CHECKOUT_BANK_ACCOUNT_WIRE_OR_CHECK', bankAccountMethodSelected };
}

export function creditCardVaultedError(error) {
    return {
        type: 'CHECKOUT_CREDITCARD_ERROR',
        error,
    };
}

export function completeCreditCards({
    lastValidCreditCardToken,
    paymentMethodApiType,
    paymentMethodToken,
}) {
    const creditCardToken =
        lastValidCreditCardToken ||
        (paymentMethodApiType === METHODS.METHOD_CREDIT_CARD && paymentMethodToken);

    if (creditCardToken) {
        return completeCreditCardAction({
            paymentMethodToken: creditCardToken,
        });
    }
    return { type: 'NO_CHANGE' };
}

// PayPal
export function payPalRemoved(lastValidCreditCardToken) {
    return emptyPaymentMethodAction({
        method: METHODS.METHOD_CREDIT_CARD,
        lastValidCreditCardToken,
    });
}

export function payPalError(error) {
    return { type: 'PAYMENT_PAYPAL_ERROR', error };
}

export function resetPayPalError() {
    return { type: 'PAYMENT_RESET_PAYPAL' };
}

export function payPalVaulted(token) {
    return savePaypalAction({
        token,
    });
}

// Wire Transfer
export function completeWireTransfer(billingAddress) {
    return saveWireTransferAction({
        billingAddress,
    });
}

export function completeOnPlatformWireTransfer(billingAddress) {
    return saveOnPlatformWireTransferAction({
        billingAddress,
    });
}

export function wireTransferFormUpdated({ field, value }) {
    return { type: 'CHECKOUT_UPDATE_NEW_BILLING_FORM', field, value };
}

export function toggleWireFormSubmissionError(showError) {
    return {
        type: 'TOGGLE_WIRE_FORM_SUBMISSION_ERROR',
        showError,
    };
}

// Check
export function completeCheck() {
    return saveCheckAction();
}

// Apple Pay
export function completeApplePaySection() {
    return saveApplePayAction();
}

export function saveApplePayPaymentRequest(applePayPaymentRequest) {
    return { type: 'SAVE_APPLE_PAY_PAYMENT_REQUEST', applePayPaymentRequest };
}

export function applePayTokenSaved(token) {
    return { type: 'SAVE_APPLE_PAY_TOKEN', token };
}

/**
 * ACH / BANK ACCOUNTS
 */
export function achFormUpdated({ field, value, isVerifying }) {
    if (billingFields.includes(field)) {
        return { type: 'CHECKOUT_UPDATE_NEW_BILLING_FORM', field, value };
    } else if (isVerifying) {
        return { type: 'CHECKOUT_UPDATE_VERIFY_BANKACCOUNT_FORM', field, value };
    } else {
        return { type: 'CHECKOUT_UPDATE_ACH_FORM', field, value };
    }
}

export function achFormUpdateBlurred(newBlurredValues) {
    return { type: 'CHECKOUT_UPDATE_ACH_FORM_BLUR', newBlurredValues };
}

export function selectBankAccount(token, paymentProcessor, bankAccountState = STATES.STATE_SAVED) {
    return {
        type: 'CHECKOUT_SELECT_BANKACCOUNT',
        bankAccountState,
        token,
        paymentProcessor,
    };
}

export function addBankAccount() {
    return { type: 'CHECKOUT_NEW_BANKACCOUNT' };
}

export function bankAccountSaved({ token, paymentProcessor }) {
    return saveBankAccountAction({
        token,
        paymentProcessor,
    });
}

export function bankAccountFormCanceled() {
    return {
        type: 'CHECKOUT_CANCEL_BANKACCOUNT',
    };
}

export function bankAccountVaultedError(error) {
    return {
        type: 'CHECKOUT_BANKACCOUNT_ERROR',
        error,
    };
}

export function bankAccountDeleted(payload) {
    const { bankAccountState, paymentMethodToken, isSelectedBankAccountDeleted } = payload;

    if (!paymentMethodToken) {
        return emptyPaymentMethodAction({
            bankAccountState,
            method: METHODS.METHOD_ACH,
        });
    } else if (isSelectedBankAccountDeleted) {
        return selectBankAccount(paymentMethodToken, bankAccountState);
    }
    return { type: 'NO_CHANGE' };
}

export function completeBankAccounts({
    lastValidBankAccountToken,
    paymentMethodApiType,
    paymentMethodToken,
    paymentProcessor,
}) {
    const bankAccountToken =
        lastValidBankAccountToken ||
        (paymentMethodApiType === METHODS.METHOD_ACH && paymentMethodToken);
    if (bankAccountToken) {
        return completeBankAccountAction({
            paymentMethodToken: bankAccountToken,
            paymentProcessor,
        });
    }
    return { type: 'NO_CHANGE' };
}

export function vatIdUpdated(value) {
    return { type: 'CHECKOUT_VAT_ID_UPDATED', value };
}

export function updateStripePaymentMethodApiType(value) {
    return saveStripePaymentMethodApiType({ paymentMethodApiType: value });
}

export function updatePaymentProcessor(value) {
    return setPaymentProcessor({ paymentProcessor: value });
}

export function eoriNumberUpdated(value) {
    return { type: 'CHECKOUT_EORI_NUMBER_UPDATED', value };
}

export function toggleBillingAddress(checked) {
    return { type: 'CHECKOUT_TOGGLE_BILLING_ADDRESS', checked };
}

export function togglePaymentSaving(isSaving) {
    return { type: 'CHECKOUT_PAYMENT_SAVING', isSaving };
}

export function togglePayPalSaving(isSaving) {
    return { type: 'CHECKOUT_PAYPAL_SAVING', isSaving };
}

export function togglePayPalRemoving(isRemoving) {
    return { type: 'CHECKOUT_PAYPAL_REMOVING', isRemoving };
}

export function toggleCreditCardListExpanded() {
    return { type: 'TOGGLE_CREDIT_CARD_LIST_EXPANDED' };
}

export function toggleBankAccountListExpanded() {
    return { type: 'TOGGLE_BANKACCOUNT_LIST_EXPANDED' };
}

export function billingAddressChanged({ field, value }) {
    return { type: 'CHECKOUT_UPDATE_NEW_BILLING_FORM', field, value };
}

export function paymentError(error) {
    return { type: 'CHECKOUT_PAYMENT_ERROR', error };
}
